/**
 * AideAgricoleStubPage
 * ─────────────────────────────────────────────────────────────────
 * Composant réutilisable pour les pages éditoriales d'aides agricoles
 * /agriculteurs/aides/[slug] (CEE secteur AGRI, FranceAgriMer, Plan
 * France 2030, PCAE, aides locales).
 *
 * Refonte 2026 : bâti sur le kit UI `audience/particulier` (ton brand
 * emerald = OK pour l'agriculture, cf. ParcoursAgricoleStubPage), avec
 * hero pleine largeur, carte "Montant" en side-slot, bandeau de repères
 * et CTA final immersif. Breadcrumb / liens / copy pointent vers le
 * profil agricole.
 *
 * SEO : injecte BreadcrumbList + FAQPage JSON-LD avec URL agricoles.
 */

import { ArrowRight, CheckCircle2, Info } from 'lucide-react';
import { Container } from '../layout/Container';
import { Button } from '../ui/button';
import { Reveal } from '../motion/Reveal';
import { BreadcrumbListLd } from '../seo/jsonld/BreadcrumbList';
import { FAQPageLd } from '../seo/jsonld/FAQPage';
import { cn } from '../../lib/cn';
import type { AideStub } from '../../config/aides-particuliers';
import {
  PARTICULIER_GRADIENT,
  PARTICULIER_SECTION,
  ParticulierCTAFinal,
  ParticulierFAQ,
  ParticulierKPIBand,
  ParticulierKeyPointsGrid,
  ParticulierPageHero,
  ParticulierSectionHeader,
  type ParticulierKpi,
} from '../audience/particulier';

interface Props {
  aide: AideStub;
}

const SITE_URL = 'https://solutionecofrance.fr';
const CONTACT_HREF = '/contact?source=agriculteurs';

/** Carte "Montant" — side-slot du hero (signature des pages aide). */
function AideAmountCard({ aide }: Props) {
  return (
    <Reveal delay={0.12}>
      <div className="relative overflow-hidden rounded-[28px] border border-stone-200 bg-paper p-7 shadow-[0_2px_28px_-10px_rgba(10,10,10,.14)]">
        <div
          aria-hidden
          className={cn('absolute inset-x-0 top-0 h-px', PARTICULIER_GRADIENT.cardTopHairline)}
        />
        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full blur-3xl',
            PARTICULIER_GRADIENT.sideCardHalo,
          )}
        />
        <div className="relative">
          <p className="eyebrow text-stone-500">Montant de l’aide</p>
          <p
            className={cn(
              'mt-2 font-display text-[2rem] font-black leading-none tracking-tight',
              PARTICULIER_GRADIENT.aideAmount,
            )}
          >
            {aide.amount}
          </p>
          <p className="mt-2 text-[13px] leading-relaxed text-stone-500">{aide.amountLabel}</p>

          <div className="mt-6 border-t border-stone-100 pt-5">
            <p className="eyebrow text-stone-500">Cumulable avec</p>
            <ul className="mt-3 space-y-2">
              {aide.cumulableWith.map((c) => (
                <li key={c} className="flex items-center gap-2 text-[13px] text-stone-700">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-brand-500" strokeWidth={2.4} />
                  {c}
                </li>
              ))}
            </ul>
          </div>

          <Button asChild variant="brand" className="mt-6 w-full">
            <a href={aide.ctaHref}>
              Estimer mes primes
              <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
          <p className="mt-3 text-center text-[11px] text-stone-400">
            Gratuit · sans engagement · 60 secondes
          </p>
        </div>
      </div>
    </Reveal>
  );
}

export function AideAgricoleStubPage({ aide }: Props) {
  const cumulableCount = aide.cumulableWith.length;

  const kpi: ParticulierKpi[] = [
    {
      value: `${cumulableCount} aides`,
      numericValue: cumulableCount,
      suffix: ' aides',
      label: 'cumulables sur un même investissement',
      source: aide.cumulableWith.slice(0, 3).join(' · '),
    },
    {
      value: '60 sec',
      label: 'pour évaluer votre potentiel',
      source: 'Diagnostic CEE agricole en ligne',
    },
    {
      value: '0 €',
      label: 'pour faire votre diagnostic',
      source: 'Sans inscription ni engagement',
    },
  ];

  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: `${SITE_URL}/` },
          { name: 'Agriculteurs', url: `${SITE_URL}/agriculteurs` },
          { name: 'Aides', url: `${SITE_URL}/agriculteurs/aides` },
          { name: aide.title, url: `${SITE_URL}/agriculteurs/aides/${aide.slug}` },
        ]}
      />
      <FAQPageLd items={aide.faq.map((f) => ({ question: f.question, answer: f.answer }))} />

      <ParticulierPageHero
        breadcrumbPrefix={[
          { name: 'Accueil', href: '/' },
          { name: 'Agriculteurs', href: '/agriculteurs' },
        ]}
        breadcrumb={[
          { name: 'Aides', href: '/agriculteurs/aides' },
          { name: aide.title },
        ]}
        eyebrow={aide.eyebrow}
        eyebrowIconName={aide.iconName}
        headline={aide.title}
        subheadline={aide.lead}
        primaryCta={{ label: aide.ctaLabel, href: aide.ctaHref }}
        secondaryCta={aide.secondaryCta}
        bullets={[
          'Dispositifs officiels à jour',
          'Sources réglementaires citées',
          'Aucune relance commerciale',
        ]}
        microPromise="Diagnostic chiffré immédiat · sans engagement"
        sideSlot={<AideAmountCard aide={aide} />}
      />

      <ParticulierKPIBand items={kpi} />

      <ParticulierKeyPointsGrid
        eyebrow="L'essentiel"
        title={`${aide.shortLabel} en 4 points`}
        description="Ce qu'il faut retenir avant de monter votre dossier — sans jargon administratif."
        points={aide.keyPoints}
        cols={2}
        align="center"
      />

      {/* ── Conditions d'éligibilité ── */}
      <section className={cn(PARTICULIER_SECTION.alternateBg, PARTICULIER_SECTION.standard)}>
        <Container>
          <ParticulierSectionHeader
            eyebrow="Éligibilité"
            title="Votre exploitation est-elle éligible ?"
            description="Les principaux critères à réunir pour mobiliser ce dispositif. Notre diagnostic vérifie votre situation exacte en quelques questions."
            align="left"
          />

          <div className="grid gap-3 sm:grid-cols-2">
            {aide.conditions.map((c) => (
              <Reveal key={c}>
                <div className="flex h-full items-start gap-3 rounded-2xl border border-stone-200 bg-paper p-4 shadow-[0_1px_3px_rgba(10,10,10,.04)]">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 ring-1 ring-inset ring-brand-200/60">
                    <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.4} />
                  </span>
                  <span className="text-[14px] leading-relaxed text-stone-700">{c}</span>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-6 flex items-start gap-3 rounded-2xl border border-stone-200 bg-stone-100/60 p-5">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-stone-500" strokeWidth={1.8} />
            <p className="text-[13px] leading-relaxed text-stone-600">
              Cette page synthétise les critères généraux de l’aide. Les conditions exactes
              dépendent de votre exploitation, du type d’investissement et de la réglementation
              en vigueur au moment de la demande. Pour une estimation chiffrée,{' '}
              <a href={aide.ctaHref} className="font-semibold text-brand-700 hover:underline">
                utilisez notre diagnostic CEE agricole
              </a>
              .
            </p>
          </div>
        </Container>
      </section>

      <ParticulierFAQ
        eyebrow={`FAQ ${aide.shortLabel}`}
        title={`Vos questions sur ${aide.shortLabel}.`}
        items={aide.faq}
      />

      <ParticulierCTAFinal
        badge="Diagnostic CEE agricole · 60 sec"
        title="Quel financement pour"
        titleAccent="votre exploitation ?"
        description={
          <>
            Estimez {aide.title} et croisez-la avec les autres dispositifs (CEE secteur AGRI, PCAE,
            FranceAgriMer) en 60 secondes. Résultat chiffré, gratuit et sans engagement.
          </>
        }
        primaryCta={{ label: aide.ctaLabel, href: aide.ctaHref }}
        secondaryCta={{ label: 'Parler à un conseiller', href: CONTACT_HREF }}
        bullets={['Sans engagement', 'Sources officielles', 'Aucune relance commerciale']}
      />
    </>
  );
}
